// Libraries
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { Controller } from '@hotwired/stimulus';

// Utilities
import apiFetch from '../utils/api-fetch';
import scrollTo from '../utils/scroll';

/* stimulusFetch: 'lazy' */

/**
 * @class FilterFormController
 * @extends Controller
 * @description Filter listings based on filter form data.
 */
class FilterFormController extends Controller {
    static targets = [
        'form',
        'input',
        'offset',
        'perPage',
        'sort',
        'next',
        'prev',
        'submit',
        'resultsCount',
        'noResults',
        'filterCount',
        'listing',
    ];
    static values = {
        total: Number,
        listingIds: Array,
        filters: Object,
    };

    /**
     * @function initialize
     * @description Set default values on intialization.
     *
     * @return {void}
     */
    initialize() {
        this.totalValue = this.formTarget.dataset.total;
        this.listingIdsValue = this.listingTargets
            .filter((listing) => !listing.classList.contains('d-none'))
            .map((listing) => listing.children[0].id);
    }

    /**
     * @function filterListings
     * @description Submit form data and update listing IDs, total and filters.
     *
     * @async
     * @return {void}
     */
    async filterListings() {
        const endpoint = this.formTarget.action;
        const method = this.formTarget.method;
        const data = new FormData(this.formTarget);
        const response = await apiFetch(endpoint, method, data);

        if (response.error) {
            return;
        }

        this.listingIdsValue = response.data.ids;
        this.totalValue = response.data.total;
        this.filtersValue = Object.fromEntries(
            Object.entries(response.data.filters).filter(([key, value]) => {
                return (
                    value !== '' &&
                    !['offset', 'per-page', 'sort', 'type'].includes(key)
                );
            })
        );

        this.filtersValueChanged();
        this.totalValueChanged();
        // scrollTo(this.element);
    }

    /**
     * @function applyFilter
     * @description Reset offset value and submit form when input field is changed.
     *
     * @param {Event} event - The event object
     * @return {void}
     */
    applyFilter(event) {
        this.offsetTarget.value = 0;

        if (event.target.dataset.reset !== null) {
            this.checkboxReset(event.target.dataset.reset);
        }

        this.submitForm();
    }

    /**
     * @function applySort
     * @description Reset offset value and submit form when sort field is changed.
     *
     * @param {Event} event - The event object
     * @return {void}
     */
    applySort(event) {
        this.offsetTarget.value = 0;
        this.sortTarget.value = event.target.value;
        this.submitForm();
    }

    /**
     * @function resetForm
     * @description Reset all form elements when reset button is clicked.
     *
     * @param {Event} event - The event object
     * @return {void}
     */
    resetForm(event) {
        event.preventDefault();
        this.offsetTarget.value = 0;
        this.formTarget.reset();
        this.submitForm();
    }

    /**
     * @function nextPage
     * @description Increment offset value and submit form when next button is clicked.
     *
     * @return {void}
     */
    nextPage() {
        const newOffset =
            parseInt(this.offsetTarget.value) +
            parseInt(this.perPageTarget.value);
        this.offsetTarget.value = newOffset;
        this.submitForm();
    }

    /**
     * @function prevPage
     * @description Decrement offset value and submit form when prev button is clicked.
     *
     * @return {void}
     */
    prevPage() {
        const newOffset =
            parseInt(this.offsetTarget.value) -
            parseInt(this.perPageTarget.value);
        this.offsetTarget.value = newOffset;
        this.submitForm();
    }

    /**
     * @function totalValueChanged
     * @description Update next/prev button status and total count text.
     *
     * @return {void}
     */
    totalValueChanged() {
        if (this.hasSubmitTarget) {
            this.submitTarget.textContent = `Show ${this.totalValue} results`;
        }

        if (this.hasResultsCountTarget) {
            this.resultsCountTarget.textContent = `Showing ${this.totalValue} results`;
        }

        if (this.hasNoResultsTarget && this.totalValue === 0) {
            this.noResultsTarget.classList.add('show');
        } else if (this.hasNoResultsTarget) {
            this.noResultsTarget.classList.remove('show');
        }

        if (
            this.hasNextTarget &&
            this.totalValue >= parseInt(this.offsetTarget.value) &&
            parseInt(this.offsetTarget.value) +
                parseInt(this.perPageTarget.value) >=
                this.totalValue
        ) {
            this.nextTarget.disabled = true;
        } else if (this.hasNextTarget) {
            this.nextTarget.disabled = false;
        }

        if (this.hasPrevTarget && parseInt(this.offsetTarget.value) === 0) {
            this.prevTarget.disabled = true;
        } else if (this.hasPrevTarget) {
            this.prevTarget.disabled = false;
        }
    }

    /**
     * @function filtersValueChanged
     * @description Update the filter value text when filters are applied.
     *
     * @return {void}
     */
    filtersValueChanged() {
        const filterCount = Object.keys(this.filtersValue).length;

        if (this.hasFilterCountTarget && filterCount > 0) {
            this.filterCountTarget.textContent = `(${filterCount})`;
        } else if (this.hasFilterCountTarget) {
            this.filterCountTarget.textContent = '';
        }
    }

    /**
     * @function listingIdsValueChanged
     * @description Update the visibility of listings when listingIds changes.
     *
     * @return {void}
     */
    listingIdsValueChanged() {
        this.listingTargets.forEach((listing) => {
            const id = listing.children[0].id;

            if (this.listingIdsValue.includes(id)) {
                listing.style.order = this.listingIdsValue.indexOf(id);
                listing.classList.remove('d-none');
            } else {
                listing.style.order = this.listingIdsValue.length;
                listing.classList.add('d-none');
            }
        });
    }

    /**
     * @function checkboxReset
     * @description Reset of a group of checkboxes.
     *
     * @param {string} name - A name attribute to match
     * @return {void}
     */
    checkboxReset(name) {
        const checkboxes = this.inputTargets.filter(
            (input) => input.getAttribute('name') === name
        );

        checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
        });
    }

    /**
     * @function submitForm
     * @description Programatically submit the form.
     *
     * @return {void}
     */
    submitForm() {
        const submitEvent = new Event('submit', {
            bubbles: true,
            cancelable: true,
        });

        this.formTarget.dispatchEvent(submitEvent);
    }
}

// Exports
export default FilterFormController;
